import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import { config } from "../../authConfig";

interface CreationPolicy {
  cutoffDate: string;
  cutoffAt: string;
  serverTime: string;
  canCreateEvents: boolean;
}

interface TransitionState {
  cutoffDate?: string;
  canCreateEvents: boolean;
}

export const SpiritNightTransitionContext = createContext<TransitionState>({ canCreateEvents: false });
export const useSpiritNightTransition = () => useContext(SpiritNightTransitionContext);

export function SpiritNightTransitionProvider({ children }: { children: ReactNode }) {
  const execute = useFetchMsal();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const [state, setState] = useState<TransitionState>({ canCreateEvents: false });

  useEffect(() => {
    let active = true;
    let cutoffTimer: ReturnType<typeof setTimeout>;
    let pending = false;
    const controller = new AbortController();
    const refresh = async () => {
      if (pending) return;
      pending = true;
      try {
        const policy: CreationPolicy = await executeRef.current("GET", `${config.rootAPIUrl}${config.rootAPIList?.eventCreationPolicy}`, undefined, controller.signal);
        if (!active) return;
        const remaining = Date.parse(policy.cutoffAt) - Date.parse(policy.serverTime);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(policy.cutoffDate) || !Number.isFinite(remaining)) throw new Error("Invalid event creation policy");
        clearTimeout(cutoffTimer);
        const allowed = policy.canCreateEvents && remaining > 0;
        setState({ cutoffDate: policy.cutoffDate, canCreateEvents: allowed });
        if (allowed && remaining <= 2147483647) {
          cutoffTimer = setTimeout(() => setState(current => ({ ...current, canCreateEvents: false })), remaining);
        }
      } catch {
        if (active) setState(current => ({ ...current, canCreateEvents: false }));
      } finally {
        pending = false;
      }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      controller.abort();
      clearInterval(interval);
      clearTimeout(cutoffTimer);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return <SpiritNightTransitionContext.Provider value={state}>{children}</SpiritNightTransitionContext.Provider>;
}

const contacts = [
  ["ATX2, ATX3", "Alex Warner"],
  ["CO", "Natalie Slevin"],
  ["DFW1, DFW2, DFW3, KAN/ARK/OK", "Courtney Colvin"],
  ["FLA, MDA, MDW", "Chelsea Santos"],
  ["HOU1", "Alex Mawn; Chelsea Santos (LSU and Lake Charles Only)"],
  ["HOU2, HOU3", "Alex Mawn"],
  ["OTX1", "Kristy Ward"],
  ["WTX/AZ/UT", "Heather Bowman"],
];

export function SpiritNightTransitionBanner() {
  const { cutoffDate } = useSpiritNightTransition();
  const dateLabel = cutoffDate ? new Date(`${cutoffDate}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : undefined;
  return (
    <Box sx={{ mx: { xs: 2, md: 3 }, mt: 2, mb: 1 }}>
      <Box component="section" aria-label="Force4Good transition notice" sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" }, p: { xs: 2, md: 3 }, bgcolor: "#fff1f3", border: "1px solid #edb4bf", borderLeft: "5px solid #bd1035", borderRadius: 2 }}>
        <CalendarMonthIcon sx={{ color: "#bd1035", fontSize: 36 }} />
        <Box sx={{ flex: 1 }}>
          <Typography component="h2" variant="h6" fontWeight={700}>Spirit Night is moving to Force4Good</Typography>
          <Typography sx={{ mt: 0.5 }}>
            {dateLabel ? <>Starting <strong>{dateLabel}</strong>, new Spirit Night events must be created in <strong>Force4Good</strong>. New event creation in this application will no longer be available.</> : "Loading the event creation cutoff date. Event creation is unavailable until the policy can be verified."}
          </Typography>
          <Typography>Please contact your <strong>FMM</strong> if you have any questions.</Typography>
        </Box>
        {dateLabel && <Chip label={`Effective ${dateLabel}`} sx={{ bgcolor: "#f8dfe5", color: "#a6192e", fontWeight: 700, maxWidth: "100%" }} />}
      </Box>
      <TableContainer sx={{ mt: 1.5, border: "1px solid #edb4bf", borderRadius: 2 }}>
        <Table size="small" aria-label="Market FMM contacts">
          <TableHead sx={{ bgcolor: "#bd1035" }}><TableRow>
            <TableCell sx={{ color: "white", fontWeight: 700, width: "32%" }}>Market</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700 }}>FMM</TableCell>
          </TableRow></TableHead>
          <TableBody>{contacts.map(([market, fmm], index) => <TableRow key={market} sx={{ bgcolor: index % 2 ? "#f4f4f6" : "white" }}>
            <TableCell component="th" scope="row">{market}</TableCell><TableCell>{fmm}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
